import React from 'react';
import Header from '../Frontend/Header';
import Footer from '../Frontend/Footer';
import { Container } from 'react-bootstrap';

const TermsOfService = () => {
    return (
        <>
            <Header />
            <Container className="py-5" style={{ marginTop: '80px', maxWidth: '900px' }}>
                <h1 className="mb-4">Terms and Conditions</h1>
                <p className="text-muted">Last updated: January 21, 2026</p>
                <p className="lead mb-5">
                    Welcome to HamaraBill! These Terms and Conditions govern your use of our billing and invoice management platform. Please read them carefully before using our services.
                </p>

                {/* 1. Agreement to Terms */}
                <section className="mb-5">
                    <h3 className="mb-3">1. Agreement to Terms</h3>
                    <p>
                        By creating an account, accessing, or using HamaraBill ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Service.
                    </p>
                    <p>
                        HamaraBill is a Software-as-a-Service (SaaS) platform that provides billing, invoicing, inventory management, and related business tools to users in India and globally.
                    </p>
                </section>

                {/* 2. Account Registration */}
                <section className="mb-5">
                    <h3 className="mb-3">2. Account Registration and Eligibility</h3>
                    <p><strong>2.1 Eligibility:</strong> You must be at least 18 years old and have the legal capacity to enter into contracts to use HamaraBill.</p>
                    <p><strong>2.2 Account Information:</strong> You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate and current.</p>
                    <p><strong>2.3 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
                    <p><strong>2.4 Business Category:</strong> During onboarding, you will select your business category (Retailer, Distributor/Wholesaler, Service Sector, FMCG, or Others). This helps us customize your experience.</p>
                </section>

                {/* 3. Subscription Plans */}
                <section className="mb-5">
                    <h3 className="mb-3">3. Subscription Plans and Billing</h3>

                    <h5 className="mt-4 mb-3">3.1 Available Plans</h5>
                    <p>HamaraBill offers the following subscription plans:</p>
                    <ul>
                        <li><strong>Free Trial:</strong> 7-day free access to all features</li>
                        {/* 1 Month & 6 Months plans commented out for now */}
                        <li><strong>1 Year Plan:</strong> ₹3,650 - Full access for 365 days (₹10/day)</li>
                        <li><strong>10 Years Plan:</strong> ₹25,000 - One-time payment for 10 years of access</li>
                    </ul>

                    <h5 className="mt-4 mb-3">3.2 Billing Cycles</h5>
                    <p><strong>1 Year Plan:</strong> Billed once for 365 days of service with no recurring renewal charges.</p>
                    <p><strong>10 Years Plan:</strong> One-time payment for 10 years of service with no recurring charges.</p>

                    <h5 className="mt-4 mb-3">3.3 Payment Processing</h5>
                    <p>All payments are processed securely through Razorpay, our payment gateway partner. We accept UPI, credit cards, debit cards, net banking, and other payment methods supported by Razorpay.</p>
                    <p>By providing payment information, you authorize HamaraBill to charge your payment method for the selected subscription plan.</p>

                    <h5 className="mt-4 mb-3">3.4 Automatic Renewal</h5>
                    <p>Monthly and yearly subscriptions automatically renew at the end of each billing period unless you cancel before the renewal date. You will be charged the then-current subscription rate.</p>

                    <h5 className="mt-4 mb-3">3.5 Price Changes</h5>
                    <p>We reserve the right to change subscription prices with 30 days' advance notice. Price changes will not affect your current billing period but will apply to subsequent renewals.</p>
                </section>

                {/* 4. Usage Limits */}
                <section className="mb-5">
                    <h3 className="mb-3">4. Usage Limits and Fair Use Policy</h3>

                    <h5 className="mt-4 mb-3">4.1 Plan Limits</h5>
                    <p>Each subscription plan has specific usage limits:</p>
                    <ul>
                        <li><strong>Invoices/Month:</strong> Maximum number of invoices (including credit notes, debit notes, and delivery challans) you can create per month</li>
                        <li><strong>Customers:</strong> Maximum number of customer records you can maintain</li>
                        <li><strong>Transactions/Month:</strong> Maximum number of financial transactions you can record per month</li>
                    </ul>

                    <h5 className="mt-4 mb-3">4.2 Exceeding Limits</h5>
                    <p>If you exceed your plan's limits, you will be prompted to upgrade to a higher plan. We may temporarily restrict access to certain features until you upgrade or your usage falls within limits.</p>

                    <h5 className="mt-4 mb-3">4.3 Fair Use</h5>
                    <p>Even on unlimited plans, you agree to use the Service reasonably and not to abuse system resources. We reserve the right to investigate and take action against accounts engaging in excessive or abusive usage.</p>
                </section>

                {/* 5. Cancellation and Refunds */}
                <section className="mb-5">
                    <h3 className="mb-3">5. Cancellation and Refund Policy</h3>

                    <h5 className="mt-4 mb-3">5.1 Cancellation</h5>
                    <p>You may cancel your subscription at any time from your account settings. Cancellation will take effect at the end of your current billing period.</p>
                    <p>Upon cancellation, you will retain access to paid features until the end of your billing period. No refunds will be provided for partial months or years.</p>

                    <h5 className="mt-4 mb-3">5.2 Strict No-Refund Policy</h5>
                    <p><strong>Strictly Non-Refundable:</strong> All subscription payments for HamaraBillBook—including 1 Year and the 10 Years Lifetime Plan—are <strong>strictly final and non-refundable</strong> once activated.</p>
                    <p>Because full digital access to invoicing features, unlimited customers, report generators, cloud data backups, and business tools is provisioned immediately upon transaction confirmation, no partial refunds, pro-rata reimbursements, or chargebacks are offered under any circumstances after taking a subscription.</p>
                    <p>Before purchasing or renewing any subscription, users are strongly encouraged to utilize the 7-day Free Trial to evaluate all features and ensure suitability for their business requirements.</p>

                    <h5 className="mt-4 mb-3">5.3 Account Termination by HamaraBill</h5>
                    <p>We reserve the right to suspend or terminate your account for violation of these Terms, non-payment, or fraudulent activity. In such cases, no refunds will be provided.</p>
                </section>

                {/* 6. Data and Privacy */}
                <section className="mb-5">
                    <h3 className="mb-3">6. Data Ownership and Privacy</h3>

                    <h5 className="mt-4 mb-3">6.1 Your Data</h5>
                    <p>You retain all ownership rights to the data you input into HamaraBill, including customer information, invoices, inventory records, and financial data.</p>

                    <h5 className="mt-4 mb-3">6.2 Data Usage</h5>
                    <p>We use your data solely to provide and improve our services. We do not sell your data to third parties. For details, please refer to our Privacy Policy.</p>

                    <h5 className="mt-4 mb-3">6.3 Data Security</h5>
                    <p>We implement industry-standard security measures to protect your data, including encryption, secure servers, and regular security audits. However, no system is 100% secure, and we cannot guarantee absolute security.</p>

                    <h5 className="mt-4 mb-3">6.4 Data Backup and Export</h5>
                    <p>We perform regular backups of your data. You can export your data at any time in standard formats (PDF, Excel, etc.) from your account dashboard.</p>

                    <h5 className="mt-4 mb-3">6.5 Data Retention</h5>
                    <p>After account cancellation, we retain your data for 90 days to allow for account reactivation. After 90 days, your data will be permanently deleted unless required by law to retain it longer.</p>
                </section>

                {/* 7. Service Features */}
                <section className="mb-5">
                    <h3 className="mb-3">7. Service Features and Availability</h3>

                    <h5 className="mt-4 mb-3">7.1 Core Features</h5>
                    <p>HamaraBill provides the following core features (availability may vary by plan):</p>
                    <ul>
                        <li>Invoice creation and management (GST-compliant)</li>
                        <li>Customer database management</li>
                        <li>Inventory tracking and categorization</li>
                        <li>Transaction recording and tracking</li>
                        <li>Business reports and analytics</li>
                        <li>Digital signatures</li>
                        <li>PDF generation and download</li>
                        <li>QR code integration</li>
                        <li>Multi-document support (Credit Notes, Debit Notes, Delivery Challans)</li>
                    </ul>

                    <h5 className="mt-4 mb-3">7.2 Service Availability</h5>
                    <p>We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. Scheduled maintenance will be announced in advance when possible.</p>

                    <h5 className="mt-4 mb-3">7.3 Feature Changes</h5>
                    <p>We may add, modify, or remove features at any time. We will notify users of significant changes via email or in-app notifications.</p>
                </section>

                {/* 8. GST Compliance */}
                <section className="mb-5">
                    <h3 className="mb-3">8. GST Compliance and Tax Matters</h3>

                    <h5 className="mt-4 mb-3">8.1 GST Features</h5>
                    <p>HamaraBill provides tools to help you create GST-compliant invoices, including GSTIN validation, HSN code support, and tax calculations. However, you are solely responsible for ensuring compliance with all applicable tax laws.</p>

                    <h5 className="mt-4 mb-3">8.2 Tax Responsibility</h5>
                    <p>You are responsible for determining and paying all applicable taxes related to your business transactions. HamaraBill is a tool to assist with billing and does not provide tax advice.</p>

                    <h5 className="mt-4 mb-3">8.3 Subscription Tax</h5>
                    <p>All subscription prices are exclusive of applicable taxes (GST, etc.). Taxes will be added to your invoice as required by law.</p>
                </section>

                {/* 9. Prohibited Uses */}
                <section className="mb-5">
                    <h3 className="mb-3">9. Prohibited Uses</h3>
                    <p>You agree NOT to use HamaraBill for:</p>
                    <ul>
                        <li>Any illegal activities or to violate any laws</li>
                        <li>Creating fraudulent invoices or documents</li>
                        <li>Uploading malicious code, viruses, or harmful content</li>
                        <li>Attempting to gain unauthorized access to our systems</li>
                        <li>Reselling or redistributing our service without authorization</li>
                        <li>Reverse engineering or copying our software</li>
                        <li>Harassing, abusing, or harming other users</li>
                        <li>Spamming or sending unsolicited communications</li>
                    </ul>
                </section>

                {/* 10. Intellectual Property */}
                <section className="mb-5">
                    <h3 className="mb-3">10. Intellectual Property Rights</h3>

                    <h5 className="mt-4 mb-3">10.1 Our IP</h5>
                    <p>HamaraBill, including its software, design, logos, and content, is protected by copyright, trademark, and other intellectual property laws. All rights are reserved.</p>

                    <h5 className="mt-4 mb-3">10.2 License Grant</h5>
                    <p>We grant you a limited, non-exclusive, non-transferable license to use HamaraBill for your business purposes in accordance with these Terms.</p>

                    <h5 className="mt-4 mb-3">10.3 User Content</h5>
                    <p>You grant us a license to use, store, and process your content solely to provide the Service. We do not claim ownership of your content.</p>
                </section>

                {/* 11. Disclaimers */}
                <section className="mb-5">
                    <h3 className="mb-3">11. Disclaimers and Limitations of Liability</h3>

                    <h5 className="mt-4 mb-3">11.1 "As Is" Service</h5>
                    <p>HamaraBill is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to merchantability, fitness for a particular purpose, or non-infringement.</p>

                    <h5 className="mt-4 mb-3">11.2 No Guarantee</h5>
                    <p>We do not guarantee that the Service will be error-free, secure, or uninterrupted. We are not responsible for data loss, business interruption, or financial losses resulting from service use or unavailability.</p>

                    <h5 className="mt-4 mb-3">11.3 Limitation of Liability</h5>
                    <p>To the maximum extent permitted by law, HamaraBill's total liability for any claims arising from these Terms or your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</p>

                    <h5 className="mt-4 mb-3">11.4 Indemnification</h5>
                    <p>You agree to indemnify and hold HamaraBill harmless from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.</p>
                </section>

                {/* 12. Third-Party Services */}
                <section className="mb-5">
                    <h3 className="mb-3">12. Third-Party Services and Integrations</h3>
                    <p>HamaraBill integrates with third-party services such as Razorpay for payments. Your use of these services is subject to their respective terms and conditions. We are not responsible for third-party service failures or issues.</p>
                </section>

                {/* 13. Modifications */}
                <section className="mb-5">
                    <h3 className="mb-3">13. Modifications to Terms</h3>
                    <p>We reserve the right to modify these Terms at any time. We will notify users of material changes via email or in-app notification at least 30 days before they take effect.</p>
                    <p>Continued use of the Service after changes take effect constitutes acceptance of the modified Terms.</p>
                </section>

                {/* 14. Governing Law */}
                <section className="mb-5">
                    <h3 className="mb-3">14. Governing Law and Dispute Resolution</h3>

                    <h5 className="mt-4 mb-3">14.1 Governing Law</h5>
                    <p>These Terms are governed by and construed in accordance with the laws of India, without regard to conflict of law principles.</p>

                    <h5 className="mt-4 mb-3">14.2 Jurisdiction</h5>
                    <p>Any disputes arising from these Terms or your use of HamaraBill shall be subject to the exclusive jurisdiction of the courts located in [Your City], India.</p>

                    <h5 className="mt-4 mb-3">14.3 Dispute Resolution</h5>
                    <p>Before filing any legal action, you agree to attempt to resolve disputes through good-faith negotiation. If negotiation fails, disputes may be resolved through arbitration in accordance with Indian arbitration laws.</p>
                </section>

                {/* 15. Miscellaneous */}
                <section className="mb-5">
                    <h3 className="mb-3">15. Miscellaneous Provisions</h3>

                    <h5 className="mt-4 mb-3">15.1 Entire Agreement</h5>
                    <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and HamaraBill regarding the Service.</p>

                    <h5 className="mt-4 mb-3">15.2 Severability</h5>
                    <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.</p>

                    <h5 className="mt-4 mb-3">15.3 Waiver</h5>
                    <p>Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.</p>

                    <h5 className="mt-4 mb-3">15.4 Assignment</h5>
                    <p>You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms without restriction.</p>

                    <h5 className="mt-4 mb-3">15.5 Force Majeure</h5>
                    <p>We are not liable for any failure or delay in performance due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, riots, or internet/telecommunications failures.</p>
                </section>

                {/* 16. Contact Information */}
                <section className="mb-5">
                    <h3 className="mb-3">16. Contact Information</h3>
                    <p>If you have questions about these Terms or the Service, please contact us:</p>
                    <ul className="list-unstyled">
                        <li><strong>Email:</strong> info.hamarabill@gmail.com</li>
                        <li><strong>Website:</strong> www.hamarabill.com</li>
                        <li><strong>Support Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST</li>
                    </ul>
                </section>

                {/* Acceptance */}
                <section className="mb-5 p-4 bg-light border-start border-primary border-4">
                    <h5 className="mb-3">Acceptance of Terms</h5>
                    <p className="mb-0">
                        By creating an account or using HamaraBill, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree, please discontinue use of the Service immediately.
                    </p>
                </section>

                <div className="text-center mt-5 pt-4 border-top">
                    <p className="text-muted">
                        <small>© 2026 HamaraBill. All rights reserved.</small>
                    </p>
                </div>
            </Container>
            <Footer />
        </>
    );
};

export default TermsOfService;
